import React from 'react';
import { HeadingDivider, HeadingDividerVariant } from '../HeadingDivider/HeadingDivider';
import { ContentBlockProps } from '../ContentBlock/ContentBlock';
import { ContentTheme } from '../Contentful/transformers/ThemeTransformer';

import { styles } from './HeadingBlock.css';
import { HEADING_FONT_VARIANTS, HeadingFontVariant } from '../HeadingDivider/HeadingFont';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { buildDecoratedText } from './util/buildDecoratedText';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';

export type HeadingBlockProps = {
  heading: string;
  subText?: string;
  headingStyle?: 'Left' | 'Centred' | 'Banner' | 'Hidden';
  contentPalette?: ContentTheme['palette'];
} & Omit<ContentBlockProps, 'children'> &
  Omit<React.ComponentProps<typeof HeadingDivider>, 'heading'>;

export const HeadingBlock = ({
  heading,
  subText,
  headingStyle,
  contentPalette,
  ...headingDividerProps
}: HeadingBlockProps) => {
  const SubTextWrapper = contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy ? CalloutFont : 'div';
  const { href, shouldOpenInNewWindow, textColor, variant, style, className } = headingDividerProps;
  const isBanner = headingStyle === 'Banner';

  const decoratedResult = typeof heading === 'string' ? buildDecoratedText(heading, styles.scaledUpText) : undefined;

  const headingDividerPropsWithDefaults = {
    heading: decoratedResult?.decoratedContent ?? heading,
    href,
    shouldOpenInNewWindow: shouldOpenInNewWindow ?? false,
    textColor: contentPalette?.heading ? contentPalette.heading : (textColor ?? contentPalette?.text ?? 'black'),
    variant: variant ?? HeadingDividerVariant.default,
    headingFontVariant: contentPalette?.headingFontVariant as HeadingFontVariant,
    shadowVariant: contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy ? 'withShadow' : undefined,
    className: classNames([
      styles.heading({
        isBanner,
        hasHighlightedText: decoratedResult?.hasDecoratedHeading ?? false,
        isCrazyFont: contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy,
      }),
      className,
    ]),
    style: {
      ...(isBanner ? { backgroundColor: contentPalette?.headingBackground } : {}),
      ...style,
    },
  };
  return (
    <div
      className={styles.headingContainer({
        headingVariant: headingStyle ?? 'Left',
        isLargeVariant: variant === HeadingDividerVariant.large,
      })}
    >
      <HeadingDivider {...headingDividerPropsWithDefaults} />
      {subText ? (
        <SubTextWrapper
          className={styles.subtext({
            headingVariant: headingStyle ?? 'Left',
            isCrazyFont: contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy,
          })}
          style={{ color: contentPalette?.text }}
        >
          {subText}
        </SubTextWrapper>
      ) : null}
    </div>
  );
};
